import React, {
  useEffect,
  useRef,
  useState,
} from 'react';

import axios from 'axios';
import moment from 'moment';

import {
  FiArrowDownRight,
  FiArrowLeft,
  FiArrowRight,
  FiArrowUpRight,
  FiCalendar,
  FiCheckCircle,
  FiDollarSign,
  FiLock,
  FiMinus,
  FiPhone,
  FiTag,
  FiUsers,
} from 'react-icons/fi';

import './Insights.css';

const baseURL = '';
const toNumber = (value) =>
  Number(value || 0);

const STYLE_COLORS = [
  '#22d865',
  '#619fff',
  '#a58cff',
  '#ffad5c',
  '#ff6577',
  '#37d8d0',
  '#e6cf57',
  '#d27cff',
  '#7d8799',
];

const PRICE_ORDER = [
  'Mini',
  'Klein',
  'Mittel',
  'Halbe Sitzung',
  'Tagessitzung',
  'Langer Tag / Projekt',
  'Ohne Preisangabe',
];

const PRICE_SHORT = {
  Mini: 'Mini',
  Klein: 'Klein',
  Mittel: 'Mittel',
  'Halbe Sitzung': '½ Tag',
  Tagessitzung: 'Tag',
  'Langer Tag / Projekt': 'Projekt',
  'Ohne Preisangabe': 'Ohne',
};

const PRICE_RANGE = {
  Mini: 'bis 150 €',
  Klein: '151–250 €',
  Mittel: '251–500 €',
  'Halbe Sitzung': '501–800 €',
  Tagessitzung: '801–1.500 €',
  'Langer Tag / Projekt':
    'über 1.500 €',
  'Ohne Preisangabe':
    'nicht hinterlegt',
};

function ChangeBadge({
  value,
  suffix = '',
}) {
  const change = toNumber(value);

  const Icon =
    change > 0
      ? FiArrowUpRight
      : change < 0
        ? FiArrowDownRight
        : FiMinus;

  const status =
    change > 0
      ? 'up'
      : change < 0
        ? 'down'
        : 'flat';

  return (
    <span
      className={`insights-change ${status}`}
    >
      <Icon />

      {change > 0 ? '+' : ''}
      {change}
      {suffix}
    </span>
  );
}

function MetricCard({
  icon,
  label,
  value,
  change,
  tone = 'green',
}) {
  return (
    <article className="insights-metric-card">
      <div
        className={`metric-icon ${tone}`}
      >
        {icon}
      </div>

      <span>{label}</span>
      <strong>{value}</strong>

      <div className="metric-comparison">
        <ChangeBadge value={change} />
        <small>zum Vormonat</small>
      </div>
    </article>
  );
}

function StylePieChart({
  items = [],
}) {
  const total = items.reduce(
    (sum, item) =>
      sum + toNumber(item.count),
    0
  );

  if (!total) {
    return (
      <article className="chart-card">
        <ChartHeading
          eyebrow="Tattoo Styles"
          title="Was wird gebucht?"
          icon={<FiTag />}
        />

        <div className="chart-empty">
          Keine Styles hinterlegt.
        </div>
      </article>
    );
  }

  let position = 0;

  const gradient = items.map(
    (item, index) => {
      const start = position;

      position +=
        (
          toNumber(item.count) /
          total
        ) * 100;

      const color =
        STYLE_COLORS[
          index % STYLE_COLORS.length
        ];

      return (
        `${color} ${start}% ` +
        `${color} ${position}%`
      );
    }
  );

  return (
    <article className="chart-card">
      <ChartHeading
        eyebrow="Tattoo Styles"
        title="Was wird gebucht?"
        icon={<FiTag />}
      />

      <div className="pie-layout">
        <div
          className="style-pie"
          style={{
            background:
              `conic-gradient(${gradient.join(',')})`,
          }}
        >
          <div>
            <strong>{total}</strong>
            <span>Termine</span>
          </div>
        </div>

        <div className="pie-legend">
          {items.map((item, index) => (
            <div key={item.name}>
              <i
                style={{
                  background:
                    STYLE_COLORS[
                      index %
                      STYLE_COLORS.length
                    ],
                }}
              />

              <p>
                <strong>{item.name}</strong>
                <small>
                  {item.count}
                  {' '}
                  {item.count === 1
                    ? 'Termin'
                    : 'Termine'}
                </small>
              </p>

              <b>{item.percentage}%</b>
            </div>
          ))}
        </div>
      </div>
    </article>
  );
}

function ChartHeading({
  eyebrow,
  title,
  icon,
}) {
  return (
    <div className="chart-heading">
      <div>
        <span>{eyebrow}</span>
        <h2>{title}</h2>
      </div>

      {icon}
    </div>
  );
}

function PriceChart({
  items = [],
  averagePrice,
}) {
  const itemMap = new Map(
    items.map((item) => [
      item.name,
      item,
    ])
  );

  const ordered = PRICE_ORDER.map(
    (name) => ({
      name,
      count: toNumber(
        itemMap.get(name)?.count
      ),
    })
  );

  const maximum = Math.max(
    1,
    ...ordered.map(
      (item) => item.count
    )
  );

  const strongest = [...ordered]
    .filter(
      (item) =>
        item.name !==
        'Ohne Preisangabe'
    )
    .sort(
      (a, b) =>
        b.count - a.count
    )[0];

  return (
    <article className="chart-card">
      <ChartHeading
        eyebrow="Preisverteilung"
        title="In welchem Segment liegen die Buchungen?"
        icon={<FiDollarSign />}
      />

      <div className="price-summary">
        <div>
          <span>Ø Preisrahmen</span>

          <strong>
            {averagePrice > 0
              ? `${averagePrice} €`
              : '–'}
          </strong>
        </div>

        <div>
          <span>Stärkstes Segment</span>

          <strong>
            {strongest?.count > 0
              ? strongest.name
              : 'Keine Angabe'}
          </strong>
        </div>
      </div>

      <div className="price-columns">
        {ordered.map((item) => {
          const height =
            item.count > 0
              ? Math.max(
                  9,
                  (
                    item.count /
                    maximum
                  ) * 100
                )
              : 0;

          return (
            <div
              className="price-column-item"
              key={item.name}
            >
              <b>
                {item.count || ''}
              </b>

              <div className="column-space">
                <div
                  className={
                    item.name ===
                    'Ohne Preisangabe'
                      ? 'price-column unknown'
                      : 'price-column'
                  }
                  style={{
                    height:
                      `${height}%`,
                  }}
                />
              </div>

              <strong>
                {PRICE_SHORT[item.name]}
              </strong>

              <small>
                {PRICE_RANGE[item.name]}
              </small>
            </div>
          );
        })}
      </div>
    </article>
  );
}


function SeasonalDemandTrend() {
  // INSIGHTS_MOBILE_FIX_V1
  const chartContainerRef = useRef(null);
  const [chartWidth, setChartWidth] = useState(900);

  useEffect(() => {
    const container = chartContainerRef.current;
    if (!container) return undefined;
    const updateWidth = () => {
      const measuredWidth = Math.round(container.getBoundingClientRect().width);
      if (measuredWidth > 0) setChartWidth(measuredWidth);
    };
    updateWidth();
    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', updateWidth);
      return () => window.removeEventListener('resize', updateWidth);
    }
    const observer = new ResizeObserver(updateWidth);
    observer.observe(container);
    return () => observer.disconnect();
  }, []);
  const months = [
    'Jan', 'Feb', 'Mär', 'Apr', 'Mai', 'Jun',
    'Jul', 'Aug', 'Sep', 'Okt', 'Nov', 'Dez',
  ];

  const series = [
    {
      name: 'Essen',
      color: '#22d865',
      values: [220, 195, 245, 240, 215, 265, 285, 275, 235, 190, 180, 195],
    },
    {
      name: 'Münster',
      color: '#619fff',
      values: [170, 155, 190, 185, 165, 205, 220, 215, 190, 155, 145, 160],
    },
    {
      name: 'Dortmund',
      color: '#a58cff',
      values: [150, 135, 170, 165, 145, 185, 200, 195, 170, 140, 130, 145],
    },
  ];

  const compact = chartWidth < 600;
  const width = chartWidth;
  const height = compact ? 260 : 320;
  const left = compact ? 34 : 54;
  const right = compact ? 14 : 18;
  const top = 18;
  const bottom = compact ? 56 : 46;
  const max = 400;
  const plotWidth = width - left - right;
  const plotHeight = height - top - bottom;
  const x = (index) => left + (index / (months.length - 1)) * plotWidth;
  const y = (value) => top + plotHeight - (value / max) * plotHeight;
  const ticks = [0, 100, 200, 300, 400];

  return (
    <article className="season-trend-card">
      <div className="season-trend-heading">
        <div>
          <span>Saisonale Orientierung</span>
          <h2>Anfrage-Trend im Jahresverlauf</h2>
          <p>Typische Nachfragebewegung für unsere drei Standorte.</p>
        </div>

        <div className="season-trend-legend">
          {series.map((item) => (
            <span key={item.name}>
              <i style={{ backgroundColor: item.color }} />
              {item.name}
            </span>
          ))}
        </div>
      </div>

      <div className="season-trend-scroll" ref={chartContainerRef}>
        <svg
          className="season-trend-chart"
          viewBox={`0 0 ${width} ${height}`}
          role="img"
          aria-label="Saisonaler Anfrage-Trend für Essen, Münster und Dortmund"
        >
          {ticks.map((tick) => (
            <g key={tick}>
              <line
                x1={left}
                x2={width - right}
                y1={y(tick)}
                y2={y(tick)}
                className="season-grid-line"
              />
              <text x={left - 12} y={y(tick) + 5} className="season-axis-value">
                {tick}
              </text>
            </g>
          ))}

          {months.map((month, index) => (
            <text
              key={month}
              x={x(index)}
              y={compact ? height - 31 + (index % 2) * 16 : height - 13}
              className="season-axis-month"
            >
              {month}
            </text>
          ))}

          {series.map((item) => {
            const points = item.values
              .map((value, index) => `${x(index)},${y(value)}`)
              .join(' ');

            return (
              <g key={item.name}>
                <polyline
                  points={points}
                  fill="none"
                  stroke={item.color}
                  className="season-trend-line"
                />
                {item.values.map((value, index) => (
                  <circle
                    key={`${item.name}-${months[index]}`}
                    cx={x(index)}
                    cy={y(value)}
                    r={compact ? 3 : 4}
                    fill="#121315"
                    stroke={item.color}
                    className="season-trend-point"
                  >
                    <title>{`${item.name}: ${months[index]} ${value}`}</title>
                  </circle>
                ))}
              </g>
            );
          })}
        </svg>
      </div>

      <div className="season-trend-note">
        <strong>Orientierung, keine Ist-Zahlen.</strong>
        <span>Die Kurven zeigen den erwartbaren Saisonverlauf; echte Monatswerte stehen oben in den Kennzahlen.</span>
      </div>
    </article>
  );
}



// AEONAS_GOOGLE_ADS_INSIGHTS_V1
function GoogleAdsPanel({ data }) {
  const tracked = toNumber(data?.trackedInquiries);
  const excluded = toNumber(data?.excludedInquiries);
  const inquiries = toNumber(data?.realInquiries);
  const customers = toNumber(data?.tattooCustomers);
  const rate = toNumber(data?.conversionRate);
  const campaigns = data?.campaigns || [];

  return (
    <section className="ads-insights-card">
      <div className="ads-insights-heading">
        <div>
          <span>Werbeerfolg</span>
          <h2>Von der Anzeige zum Tattoo-Termin</h2>
        </div>
        <small>Tracking seit 20.10.2026</small>
      </div>

      {!tracked ? (
        <div className="ads-insights-empty">
          Für diesen Monat liegen noch keine erfassten Google-Ads-Anfragen vor.
        </div>
      ) : (
        <>
          <div className="ads-metric-grid">
            <div>
              <small>Erfasst</small>
              <span>Werbe-Anfragen</span>
              <strong>{tracked}</strong>
            </div>
            <div>
              <small>Qualifiziert</small>
              <span>Echte Interessenten</span>
              <strong>{inquiries}</strong>
            </div>
            <div>
              <small>Gewonnen</small>
              <span>Gebuchte Kunden</span>
              <strong>{customers}</strong>
            </div>
            <div>
              <small>Ergebnis</small>
              <span>Abschlussquote</span>
              <strong>{rate}%</strong>
            </div>
          </div>

          {excluded > 0 && (
            <p className="ads-excluded-note">
              Davon wurden {excluded} als „Keine Anfrage“ aussortiert.
            </p>
          )}

          <div className="ads-campaign-table-wrap">
            <table className="ads-campaign-table">
              <thead>
                <tr>
                  <th>Kampagne</th>
                  <th>Interessenten</th>
                  <th>Kunden</th>
                  <th>Abschluss</th>
                </tr>
              </thead>
              <tbody>
                {campaigns.map((item) => (
                  <tr key={item.campaign}>
                    <td>{item.campaign}</td>
                    <td>{item.inquiries}</td>
                    <td>{item.tattooCustomers}</td>
                    <td>{item.conversionRate}%</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </section>
  );
}

function Insights({
  selectedLocationId,
}) {
  const [months, setMonths] =
    useState([]);

  const [, setQuality] =
    useState({});

  const [adsMonths, setAdsMonths] =
    useState([]);

  const [
    selectedMonthIndex,
    setSelectedMonthIndex,
  ] = useState(0);

  const [loading, setLoading] =
    useState(true);

  const [error, setError] =
    useState('');

  const [pin, setPin] =
    useState('');

  const [pinError, setPinError] =
    useState('');

  const [isUnlocked, setIsUnlocked] =
    useState(
      sessionStorage.getItem(
        'insightsUnlocked'
      ) === 'true'
    );

  const INSIGHTS_PIN = '8586';

  useEffect(() => {
    if (
      !selectedLocationId ||
      !isUnlocked
    ) {
      return;
    }

    const loadInsights = async () => {
      try {
        setLoading(true);
        setError('');

        const response =
          await axios.get(
            `${baseURL}/api/insights/consultations`,
            {
              params: {
                locationId:
                  selectedLocationId,
              },
            }
          );

        const receivedMonths =
          response.data?.months || [];

        setMonths(receivedMonths);

        setQuality(
          response.data?.dataQuality ||
          {}
        );

        setSelectedMonthIndex(
          Math.max(
            receivedMonths.length - 1,
            0
          )
        );
      } catch (loadError) {
        console.error(loadError);

        setError(
          'Insights konnten nicht geladen werden.'
        );

        setMonths([]);
      } finally {
        setLoading(false);
      }
    };

    loadInsights();
  }, [
    selectedLocationId,
    isUnlocked,
  ]);

  useEffect(() => {
    if (!selectedLocationId || !isUnlocked) return;

    const controller = new AbortController();

    axios.get(`${baseURL}/api/insights/google-ads`, {
      signal: controller.signal,
      timeout: 20000,
      params: { locationId: selectedLocationId },
    }).then((response) => {
      if (!controller.signal.aborted) {
        setAdsMonths(response.data?.months || []);
      }
    }).catch((adsError) => {
      if (!controller.signal.aborted) {
        console.error('Google Ads insights:', adsError);
        setAdsMonths([]);
      }
    });

    return () => controller.abort();
  }, [selectedLocationId, isUnlocked]);

  const unlock = (candidatePin = pin) => {
    // Preserve button/Enter support while accepting the freshly entered PIN.
    const enteredPin = typeof candidatePin === 'string' ? candidatePin : pin;
    if (enteredPin !== INSIGHTS_PIN) {
      setPinError('Die PIN ist nicht korrekt.');
      setPin('');
      return;
    }

    sessionStorage.setItem(
      'insightsUnlocked',
      'true'
    );

    setIsUnlocked(true);
    setPin('');
    setPinError('');
  };

  const logout = () => {
    sessionStorage.removeItem(
      'insightsUnlocked'
    );

    setIsUnlocked(false);
    setPin('');
    setPinError('');
    setMonths([]);
  };

  if (!isUnlocked) {
    return (
      <div className="insights-auth-screen-v3">
        <form
          className="insights-auth-card-v3"
          onSubmit={(event) => {
            event.preventDefault();
            unlock();
          }}
        >
          <div className="insights-auth-icon-v3">
            <FiLock />
          </div>
          <span className="insights-auth-eyebrow-v3">
            Studio Performance
          </span>
          <h1>Insights</h1>
          <p>
            Gib deine vierstellige PIN ein,
            um die Auswertung zu öffnen.
          </p>

          <label htmlFor="insights-pin-v3">
            PIN
          </label>
          <input
            id="insights-pin-v3"
            type="password"
            inputMode="numeric"
            pattern="[0-9]*"
            maxLength={4}
            autoComplete="one-time-code"
            enterKeyHint="done"
            value={pin}
            onChange={(event) => {
              const nextPin = event.target.value.replace(/\D/g, '').slice(0, 4);
              setPin(nextPin);
              setPinError('');
              if (nextPin.length === INSIGHTS_PIN.length) {
                unlock(nextPin);
              }
            }}
            placeholder="••••"
            aria-invalid={Boolean(pinError)}
            aria-describedby={
              pinError
                ? 'insights-pin-error-v3'
                : undefined
            }
            autoFocus
          />

          <div
            id="insights-pin-error-v3"
            className="insights-auth-error-v3"
            aria-live="polite"
          >
            {pinError}
          </div>

          <button
            type="submit"
            disabled={pin.length !== 4}
          >
            Insights öffnen
          </button>

          <small>
            Nur für autorisierte Mitarbeitende
          </small>
        </form>
      </div>
    );
  }

  const current =
    months[selectedMonthIndex];

  const previous =
    months[selectedMonthIndex - 1];

  const inquiries =
    toNumber(current?.inquiries);

  const bookings =
    toNumber(
      current?.tattooBookings
    );

  const conversions =
    toNumber(current?.conversions);

  const direct =
    toNumber(
      current?.directBookings
    );

  const convertedSessions =
    toNumber(
      current
        ?.convertedBookingSessions
    );

  const rate =
    toNumber(
      current?.conversionRate
    );

  const previousInquiries =
    toNumber(previous?.inquiries);

  const previousBookings =
    toNumber(
      previous?.tattooBookings
    );

  const previousConversions =
    toNumber(
      previous?.conversions
    );

  const previousDirect =
    toNumber(
      previous?.directBookings
    );

  const previousRate =
    toNumber(
      previous?.conversionRate
    );

  const currentAds =
    adsMonths.find((item) =>
      String(item.month).slice(0, 7) ===
      String(current?.month).slice(0, 7)
    );

  return (
    <div className="insights-page">
      <header className="insights-header">
        <div>
          <span>Studio Performance</span>
          <h1>Insights</h1>

          <p>
            Anfragen, Buchungen,
            Styles und Preise.
          </p>
        </div>

        <button
          onClick={logout}
          className="lock-button"
        >
          <FiLock />
        </button>
      </header>

      {loading ? (
        <div className="insights-empty">
          Lade Insights...
        </div>
      ) : error ? (
        <div className="insights-empty error">
          {error}
        </div>
      ) : !current ? (
        <div className="insights-empty">
          Keine Daten vorhanden.
        </div>
      ) : (
        <>
          <section className="insights-month-navigation" aria-label="Monatsauswahl">
            <button
              type="button"
              aria-label="Vorheriger Monat"
              disabled={
                selectedMonthIndex <= 0
              }
              onClick={() =>
                setSelectedMonthIndex(
                  (index) => Math.max(0, index - 1)
                )
              }
            >
              <FiArrowLeft />
            </button>

            <div>
              <span>
                Ausgewählter Monat
              </span>

              <h2>
                {moment(
                  current.month
                ).format('MMMM YYYY')}
              </h2>

              <small>
                Nach Erstellungsdatum
              </small>
            </div>

            <button
              type="button"
              aria-label="Nächster Monat"
              disabled={
                selectedMonthIndex >=
                months.length - 1
              }
              onClick={() =>
                setSelectedMonthIndex(
                  (index) => Math.min(months.length - 1, index + 1)
                )
              }
            >
              <FiArrowRight />
            </button>
          </section>

          <section className="metric-grid">
            <MetricCard
              icon={<FiUsers />}
              label="Neue Anfragen"
              value={inquiries}
              change={
                inquiries -
                previousInquiries
              }
            />

            <MetricCard
              icon={<FiCalendar />}
              label="Tattoo-Buchungen"
              value={bookings}
              change={
                bookings -
                previousBookings
              }
              tone="purple"
            />

            <MetricCard
              icon={<FiCheckCircle />}
              label="Umgewandelt"
              value={conversions}
              change={
                conversions -
                previousConversions
              }
              tone="blue"
            />

            <MetricCard
              icon={<FiPhone />}
              label="Direkt gebucht"
              value={direct}
              change={
                direct -
                previousDirect
              }
              tone="orange"
            />
          </section>

          <section className="detail-grid">
            <article className="conversion-card">
              <div className="panel-heading">
                <div>
                  <span>Conversion</span>
                  <h2>
                    Anfragen zu Kunden
                  </h2>
                </div>

                <ChangeBadge
                  value={
                    rate -
                    previousRate
                  }
                  suffix=" Pkt."
                />
              </div>

              <div className="conversion-layout">
                <div
                  className="conversion-ring"
                  style={{
                    '--rate':
                      `${Math.min(
                        100,
                        rate
                      )}%`,
                  }}
                >
                  <div>
                    <strong>{rate}%</strong>
                    <span>Quote</span>
                  </div>
                </div>

                <div className="conversion-copy">
                  <strong>
                    {conversions} von
                    {' '}
                    {inquiries} Anfragen
                  </strong>

                  <p>
                    wurden mindestens einmal
                    als Tattoo-Termin gebucht.
                  </p>

                  <small>
                    Daraus entstanden
                    {' '}
                    {convertedSessions}
                    {' '}
                    Tattoo-Sitzungen.
                  </small>
                </div>
              </div>
            </article>

            <article className="origin-card">
              <div className="panel-heading">
                <div>
                  <span>
                    Buchungsherkunft
                  </span>

                  <h2>
                    Zusammensetzung
                  </h2>
                </div>
              </div>

              <div className="origin-list">
                <div>
                  <i className="blue" />
                  <p>
                    <strong>
                      Aus Anfragen
                    </strong>
                    <small>
                      Tattoo-Sitzungen
                    </small>
                  </p>
                  <b>{convertedSessions}</b>
                </div>

                <div>
                  <i className="orange" />
                  <p>
                    <strong>
                      Direkt gebucht
                    </strong>
                    <small>
                      ohne Anfrage
                    </small>
                  </p>
                  <b>{direct}</b>
                </div>

                <div>
                  <i className="green" />
                  <p>
                    <strong>
                      Alle Buchungen
                    </strong>
                    <small>
                      im gewählten Monat
                    </small>
                  </p>
                  <b>{bookings}</b>
                </div>
              </div>
            </article>
          </section>

          <SeasonalDemandTrend />

          <GoogleAdsPanel data={currentAds} />

          <section className="chart-grid">
            <StylePieChart
              items={
                current.styles || []
              }
            />

            <PriceChart
              items={
                current
                  .priceCategories || []
              }
              averagePrice={
                toNumber(
                  current.averagePrice
                )
              }
            />
          </section>
        </>
      )}
    </div>
  );
}

export default Insights;
